import { Box, Checkbox, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { cegidInvoiceDialogMessages as messages } from "./messages";
import { CegidDraftLine, CegidInvoiceDraft } from "./types";

interface CegidDraftReviewProps {
  draft: CegidInvoiceDraft;
  reviewed: boolean;
  onReviewedChange: (reviewed: boolean) => void;
}

const money = (value: number) => `${value.toFixed(2)} €`;
const percentage = (value: number | null) => (value === null ? "—" : `${value}%`);

const Cell = ({
  value,
  expected,
  matches,
}: {
  value: string;
  expected?: string | null;
  matches: boolean;
}) => {
  const intl = useIntl();

  return (
    <Box display="grid">
      <Text size={2} color={matches ? "default1" : "critical1"}>
        {value}
      </Text>
      {!matches && expected && (
        <Text size={1} color="critical1">
          {intl.formatMessage(messages.expected, { value: expected })}
        </Text>
      )}
    </Box>
  );
};

const lineCells = (line: CegidDraftLine) => ({
  quantity: line.expectedQuantity === null || line.quantity === line.expectedQuantity,
  unitPrice: line.expectedUnitPrice === null || line.unitPrice === line.expectedUnitPrice,
  tax: line.expectedTaxPercentage === null || line.taxPercentage === line.expectedTaxPercentage,
});

const CegidDraftReview = ({ draft, reviewed, onReviewedChange }: CegidDraftReviewProps) => {
  const intl = useIntl();
  const columns = "1fr 2fr 0.6fr 1.2fr 0.7fr 1fr";

  return (
    <Box display="grid" gap={4} data-test-id="cegid-draft-review">
      <Box backgroundColor="warning1" padding={3} borderRadius={3}>
        <Text size={2} fontWeight="medium">
          {intl.formatMessage(messages.draftBanner, { documentId: draft.documentId })}
        </Text>
      </Box>

      <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={3}>
        <Box display="grid" gap={1}>
          <Text size={2} color="default2">
            {intl.formatMessage(messages.documentTypeLabel)}
          </Text>
          <Text fontWeight="medium">
            {draft.documentType}
            {draft.paymentMechanism ? ` · ${draft.paymentMechanism}` : ""}
            {draft.date ? ` · ${draft.date}` : ""}
          </Text>
        </Box>
        <Box display="grid" gap={1}>
          <Text size={2} color="default2">
            {intl.formatMessage(messages.customer)}
          </Text>
          <Text fontWeight="medium">
            {draft.customerBusinessName} · NIF {draft.customerTaxRegistrationNumber}
          </Text>
          {draft.customerAddress && (
            <Text size={2} color="default2">
              {draft.customerAddress}
            </Text>
          )}
        </Box>
      </Box>

      <Box display="grid">
        <Box
          display="grid"
          __gridTemplateColumns={columns}
          gap={2}
          paddingY={2}
          borderBottomWidth={1}
          borderBottomStyle="solid"
          borderColor="default1"
        >
          {[
            messages.colSku,
            messages.colDescription,
            messages.colQuantity,
            messages.colUnitPrice,
            messages.colVat,
            messages.colTotal,
          ].map(message => (
            <Text key={message.id} size={2} color="default2" fontWeight="medium">
              {intl.formatMessage(message)}
            </Text>
          ))}
        </Box>
        {draft.lines.map((line, index) => {
          const ok = lineCells(line);

          return (
            <Box
              key={`${line.itemCode}-${index}`}
              display="grid"
              __gridTemplateColumns={columns}
              gap={2}
              paddingY={2}
              borderBottomWidth={1}
              borderBottomStyle="solid"
              borderColor="default1"
              backgroundColor={line.matches ? undefined : "critical1"}
            >
              <Text size={2}>{line.itemCode}</Text>
              <Text size={2}>{line.description}</Text>
              <Cell
                value={String(line.quantity)}
                expected={line.expectedQuantity?.toString()}
                matches={ok.quantity}
              />
              <Cell
                value={money(line.unitPrice)}
                expected={line.expectedUnitPrice === null ? null : money(line.expectedUnitPrice)}
                matches={ok.unitPrice}
              />
              <Cell
                value={percentage(line.taxPercentage)}
                expected={percentage(line.expectedTaxPercentage)}
                matches={ok.tax}
              />
              <Text size={2}>{money(line.lineTotal)}</Text>
            </Box>
          );
        })}
      </Box>

      <Box display="grid" gap={1} justifyContent="end" __gridTemplateColumns="auto auto">
        <Text size={2} color="default2">
          {intl.formatMessage(messages.cegidTotal)}
        </Text>
        <Text fontWeight="bold">{money(draft.cegidTotal)}</Text>
        {draft.cegidReportedTotal !== null && (
          <>
            <Text size={2} color="default2">
              {intl.formatMessage(messages.cegidReportedTotal)}
            </Text>
            <Text fontWeight="bold">{money(draft.cegidReportedTotal)}</Text>
          </>
        )}
        <Text size={2} color="default2">
          {intl.formatMessage(messages.orderTotal)}
        </Text>
        <Text fontWeight="bold">{money(draft.orderTotal)}</Text>
      </Box>

      {draft.problems.length ? (
        <Box backgroundColor="critical1" padding={3} borderRadius={3} display="grid" gap={1}>
          <Text fontWeight="bold" color="critical1">
            {intl.formatMessage(messages.problemsHeader)}
          </Text>
          {draft.problems.map((problem, index) => (
            <Text key={index} size={2} color="critical1">
              • {problem}
            </Text>
          ))}
        </Box>
      ) : (
        <Text color="success1" fontWeight="medium">
          {intl.formatMessage(messages.allChecksPassed)}
        </Text>
      )}

      {draft.pdfUrl && (
        <a href={draft.pdfUrl} target="_blank" rel="noreferrer">
          <Text color="accent1" textDecoration="underline">
            {intl.formatMessage(messages.openDraftPdf)}
          </Text>
        </a>
      )}

      {draft.canFinalize && (
        <Checkbox
          checked={reviewed}
          onCheckedChange={checked => onReviewedChange(!!checked)}
          data-test-id="cegid-reviewed"
        >
          <Text size={2} fontWeight="medium">
            {intl.formatMessage(messages.reviewedConfirmation)}
          </Text>
        </Checkbox>
      )}
    </Box>
  );
};

CegidDraftReview.displayName = "CegidDraftReview";
export default CegidDraftReview;
